import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, BoxProps, Typography } from '@mui/material';

// Declare adsbygoogle on window to avoid TypeScript errors
declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

interface AdSenseProps extends Omit<BoxProps, 'className' | 'style'> {
  /**
   * Your AdSense ad slot ID (e.g., "1234567890")
   * This is the ad unit ID from your AdSense dashboard
   */
  adSlot: string;
  /**
   * Your AdSense publisher ID (default: "ca-pub-4399752552614912")
   */
  adClient?: string;
  /**
   * Ad format (e.g., "auto", "rectangle", "vertical")
   */
  adFormat?: string;
  /**
   * Ad layout (e.g., "in-article", "native")
   */
  adLayout?: string;
  /**
   * Full width responsive ad
   */
  fullWidthResponsive?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Ad dimensions
   */
  adLayoutKey?: string;
  /**
   * Optional key to force ad reload when changed (e.g., game ID, page identifier)
   * Changing this value will reload the ad
   */
  reloadKey?: string | number;
}

/**
 * AdSense component for displaying Google AdSense ads in your React app
 * 
 * Usage:
 * ```tsx
 * <AdSense 
 *   adSlot="1234567890" 
 *   adFormat="auto" 
 *   fullWidthResponsive 
 * />
 * ```
 */
const AdSense: React.FC<AdSenseProps> = ({
  adSlot,
  adClient = 'ca-pub-4399752552614912',
  adFormat = 'auto',
  adLayout,
  fullWidthResponsive = false,
  adLayoutKey,
  className,
  style,
  reloadKey,
  ...boxProps
}) => {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const adInitializedRef = useRef<string | null>(null);
  const isDev = process.env.NODE_ENV === 'development';

  // Create a unique key based on route and reloadKey
  const adKey = `${location.pathname}-${adSlot}-${reloadKey ?? ''}`;

  useEffect(() => {
    // Only load ads in production
    if (isDev) return;

    // Reset initialization flag when key changes to force ad reload
    const previousKey = adInitializedRef.current;
    adInitializedRef.current = null;
    
    // Small delay to ensure DOM is ready after route/reloadKey change
    const timer = setTimeout(() => {
      try {
        if (containerRef.current && previousKey !== adKey) {
          // Push ad to adsbygoogle array to trigger ad load/reload
          if (window.adsbygoogle && Array.isArray(window.adsbygoogle)) {
            window.adsbygoogle.push({});
            adInitializedRef.current = adKey;
          }
        }
      } catch (err) {
        console.error('AdSense error:', err);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [adKey, isDev]);

  // Determine ad height and width based on format
  const getAdHeight = () => {
    if (adFormat === 'horizontal') return '90px';
    if (adFormat === 'vertical') return '250px';
    return '100px'; // default
  };

  const getAdWidth = () => {
    if (adFormat === 'horizontal') return '728px';
    if (adFormat === 'vertical') return '300px';
    return '100%'; // default
  };

  const adHeight = getAdHeight();
  const adWidth = getAdWidth();

  const adStyle: React.CSSProperties = {
    display: 'block',
    textAlign: 'center' as const,
    height: adHeight,
    minHeight: adHeight,
    maxHeight: adHeight,
    ...style,
  };

  return (
    <Box
      {...boxProps}
      className={className}
      sx={{
        ...boxProps.sx,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: adHeight,
        minHeight: adHeight,
        maxHeight: adHeight,
        width: style?.width || '100%',
        maxWidth: style?.maxWidth || (adFormat === 'horizontal' ? '728px' : adFormat === 'vertical' ? '300px' : 'none'),
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div 
        ref={containerRef} 
        key={adKey} 
        style={{ 
          width: '100%', 
          height: adHeight,
          minHeight: adHeight,
          maxHeight: adHeight,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {!isDev && (
          <ins
            className="adsbygoogle"
            style={adStyle}
            data-ad-client={adClient}
            data-ad-slot={adSlot}
            data-ad-format={adFormat}
            data-full-width-responsive={fullWidthResponsive ? 'true' : 'false'}
            {...(adLayout && { 'data-ad-layout': adLayout })}
            {...(adLayoutKey && { 'data-ad-layout-key': adLayoutKey })}
          />
        )}
        {isDev && (
          <Box
            sx={{
              width: style?.width || '100%',
              maxWidth: style?.maxWidth || (adFormat === 'horizontal' ? '728px' : adFormat === 'vertical' ? '300px' : 'none'),
              height: adHeight,
              minHeight: adHeight,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#e0e0e0',
              border: '3px dashed',
              borderColor: '#9e9e9e',
              borderRadius: 2,
              position: 'relative',
              margin: '0 auto',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#616161',
                fontWeight: 'bold',
                fontSize: adFormat === 'horizontal' ? '1rem' : '1.5rem',
                textTransform: 'uppercase',
                letterSpacing: '2px',
              }}
            >
              AD
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#757575',
                fontSize: adFormat === 'horizontal' ? '0.65rem' : '0.75rem',
                mt: 0.5,
                fontFamily: 'monospace',
              }}
            >
              {adFormat === 'horizontal' ? '728 × 90' : adFormat === 'vertical' ? '300 × 250' : 'Ad Space'}
            </Typography>
          </Box>
        )}
      </div>
    </Box>
  );
};

export default AdSense;
